/* stylelint-disable no-descending-specificity */
<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import { DEFAULT_IMG } from '@/constants/image';
    import { formatCurrency } from '@/helpers/formatting';
    import { type EventTypeTyping, type GuestTypePriceTyping, type PackageTyping, type ProductTyping as WidgetProductTyping, type SpaceTyping as WidgetSpaceTyping } from '@/typings/widget';
    import AdvancedImage from '@shared/components/ui/advanced-image.vue';
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { ProductPricingStrategy } from '@shared/constants/payments';
    import { DateTime } from '@shared/helpers';
    import { combineDescriptions, filterNotes } from '@shared/helpers/notes';
    import { formatTextToHtml } from '@shared/helpers/text';
    import { type MediaTyping, type NoteTyping, type ProductTyping, type SpaceTyping, type TimeSlotProductTyping, type TimeSlotSpaceTyping } from '@shared/typings';
    import { type Ref } from 'vue';

    const props = defineProps<{
        packageItem: PackageTyping
        isOnBusinessPath?: boolean
    }>();

    const emit = defineEmits<{(event: 'showBookView' | 'goBack' | 'showSpaces' | 'showProducts')}>();

    const { t, locale } = useI18n();

    // Stores
    const { guestAmount, eventTypeId } = storeToRefs(useWidgetPathStore());
    const {
        spaceIds,
        productIds,
        selectedDate,
        selectedTimeRange,
        guestAmount: businessGuestAmount,
    } = storeToRefs(useWidgetBusinessStore());
    const { venueEventTypes, venueSpaces, venueProducts } = storeToRefs(useWidgetVenueStore());

    // Refs
    const expandNotes: Ref<boolean> = ref(false);
    const packageDetail: Ref<HTMLElement|null> = ref(null);
    const packageNotes: Ref<HTMLElement|null> = ref(null);
    const price = useTemplateRef<HTMLDivElement>('price');
    const topAction = useTemplateRef<HTMLDivElement>('topAction');
    const priceIsVisible = shallowRef<boolean>(true);
    const topActionIsVisible = shallowRef<boolean>(false);
    const showReadMode: Ref<boolean> = ref(false);
    const expandedProducts: Ref<Record<string, boolean>> = ref({});

    useIntersectionObserver(price, ([entry]) => {
        priceIsVisible.value = entry?.isIntersecting || false;
    });

    // On the business path there is no price to observe, so we track the top action
    // (book button) instead. It is hidden on mobile, so the fixed book bar always
    // shows there, and appears on scroll once the top button leaves the viewport.
    useIntersectionObserver(topAction, ([entry]) => {
        topActionIsVisible.value = entry?.isIntersecting || false;
    });

    // Computed
    // eslint-disable-next-line vue/max-len
    const isComingFromBusinessPath: ComputedRef<boolean> = computed(() => Boolean(selectedDate.value.length || spaceIds.value.length || productIds.value.length));

    // Show the fixed book bar regardless of price: on the business path the price is
    // not calculable (NaN), so we rely on the top action visibility instead of the price.
    const showBookBar: ComputedRef<boolean> = computed(() =>
        (isComingFromBusinessPath.value || props.isOnBusinessPath) ? !topActionIsVisible.value : !priceIsVisible.value);
    const hasAnyTimeslots: ComputedRef<boolean> = computed(() =>
        props.packageItem?.programme?.some(program => Array.isArray(program.timeslots) && program.timeslots.length) ?? false);
    const selectedEventType: ComputedRef<EventTypeTyping | undefined> = computed(() => venueEventTypes.value.find(eventType => eventType.id === eventTypeId.value));
    const selectedSpaces: ComputedRef<WidgetSpaceTyping[]> = computed(() => venueSpaces.value.filter(space => spaceIds.value.includes(space.id)));
    const selectedProducts: ComputedRef<WidgetProductTyping[]> = computed(() => venueProducts.value.filter(product => productIds.value.includes(product.id)));
    const selectedCover: ComputedRef<MediaTyping | null> = computed(() => props.packageItem.cover ?? selectedEventType.value?.cover ?? null);
    const filteredNotes: ComputedRef<NoteTyping[]> = computed(() => filterNotes(props.packageItem.notes ?? [], 3));
    const unitPriceFilteredAmounts: ComputedRef<(product: WidgetProductTyping) => GuestTypePriceTyping[] | undefined> = computed(() =>
        (product: WidgetProductTyping) => product.guestTypePrices?.filter(prc =>
            product.guestTypes.some(guestType =>
                prc.guestType.id === guestType.id)) ?? []);

    // Methods

    // Truncates the combined notes to a visible-character limit while preserving the
    // line breaks that separate each note, so the collapsed preview keeps the same
    // HTML structure (blank spaces between notes) as the expanded view.
    const truncateNotes = (text: string, maxLength: number): string => {
        let visibleCount = 0;
        let result = '';

        for (const char of text) {
            if (visibleCount >= maxLength) break;
            if (char !== '\n') visibleCount += 1;
            result += char;
        }

        return result.trimEnd();
    };

    const setNotesText = (): void => {
        const maxLength = 246;
        const combinedDescriptions = combineDescriptions(filteredNotes.value);
        const normalizedLength = combinedDescriptions.replace(/\s+/gu, ' ')
            .trim().length;

        showReadMode.value = normalizedLength > maxLength;

        if (!packageNotes.value) return;

        const shouldTruncate = !expandNotes.value && normalizedLength > maxLength;

        packageNotes.value.innerHTML = shouldTruncate
            ? `${formatTextToHtml(truncateNotes(combinedDescriptions, maxLength))}....`
            : formatTextToHtml(combinedDescriptions);
    };

    const toggleNotes = (): void => {
        expandNotes.value = !expandNotes.value;
    };

    const shouldShowProductReadMore = (description: string): boolean => {
        const maxLength = 50;

        // Show read more if: has multiple lines OR text is too long
        const hasMultipleLines = description.includes('\n');
        const normalizedLength = description.replace(/\s+/gu, ' ')
            .trim().length;
        return hasMultipleLines || normalizedLength > maxLength;
    };

    const getProductNotes = (description: string, isExpanded: boolean): string => {
        const maxLength = 50;

        if (!description) return '';

        if (isExpanded) {
            return formatTextToHtml(description);
        }

        const hasMultipleLines = description.includes('\n');
        const normalizedLength = description.replace(/\s+/gu, ' ')
            .trim().length;

        // If single line and fits within maxLength, show full text
        if (!hasMultipleLines && normalizedLength <= maxLength) {
            return formatTextToHtml(description);
        }

        // If has multiple lines, show only first line (truncated if needed)
        // If single line but too long, truncate it
        const [firstLine] = description.split('\n');
        const firstLineNormalized = firstLine.replace(/\s+/gu, ' ')
            .trim();

        if (firstLineNormalized.length <= maxLength) {
            return `${firstLineNormalized}....`;
        }

        return `${firstLineNormalized.slice(0, maxLength)}....`;
    };

    const toggleProductNotes = (productId: string): void => {
        expandedProducts.value[productId] = !expandedProducts.value[productId];
    };

    const getTimeslotSpaceName = (timeslotSpace: TimeSlotSpaceTyping & { space?: SpaceTyping}): string => (timeslotSpace.overrideName ? timeslotSpace.overrideName : timeslotSpace.space?.name) || '';

    const getTimeslotProductName = (timeslotProduct: TimeSlotProductTyping & { product?: ProductTyping}): string => (timeslotProduct.overrideName ? timeslotProduct.overrideName : timeslotProduct.product?.name) || '';

    const getTimeslotProductNotes = (timeslotProduct: TimeSlotProductTyping & { product?: ProductTyping }): string => {
        const timeslotProductFilteredNotes = filterNotes(timeslotProduct.notes ?? [], 3);
        const productFilteredNotes = filterNotes(timeslotProduct.product?.notes ?? [], 3);

        return (timeslotProductFilteredNotes ? combineDescriptions(timeslotProductFilteredNotes) : combineDescriptions(productFilteredNotes)) || '';
    };

    const getTimeslotProductCover = (timeslotProduct: TimeSlotProductTyping & { product?: ProductTyping}): MediaTyping | null =>
        timeslotProduct.overrideCover ?? timeslotProduct.product?.cover ?? null;
    const getTimeslotProducts = (items: unknown[]): TimeSlotProductTyping[] => items.map(item => {
        const timeslotProduct = item as TimeSlotProductTyping;

        return {
            ...timeslotProduct,
            overrideName: getTimeslotProductName(timeslotProduct),
            overrideDescription: getTimeslotProductNotes(timeslotProduct),
            overrideCover: getTimeslotProductCover(timeslotProduct),
        };
    });

    const calculatePackageDetailHeight = (): void => {
        if (packageDetail.value) {
            const shadowRoot = document.querySelector('constell-widget')?.shadowRoot;
            const mainContent = shadowRoot?.getElementById('main-content');
            if (mainContent) packageDetail.value.style.minHeight = `${mainContent.clientHeight}px`;
        }
    };

    // Hooks
    onMounted(() => {
        if (filteredNotes.value) setNotesText();
        calculatePackageDetailHeight();
    });

    // Watchers
    watch(() => expandNotes.value, () => {
        setNotesText();
    });
</script>

<template>
    <div
        ref="packageDetail"
        class="package-detail"
    >
        <div class="package-detail__header">
            <BackButton
                :on-click="() => emit('goBack')"
            />
        </div>
        <div class="package-detail__content">
            <div class="package-detail__img">
                <AdvancedImage
                    v-if="selectedCover"
                    :media="selectedCover"
                    :preview="selectedCover ? null : DEFAULT_IMG"
                    :asset-width="850"
                    no-crop
                    aspect-ratio="2:1"
                />
            </div>
            <div class="package-detail__body">
                <div class="package-detail__title">
                    <h2>
                        {{ isComingFromBusinessPath ? selectedEventType?.name : packageItem.name }}
                    </h2>
                    <div>
                        <div
                            v-if="!isComingFromBusinessPath"
                            ref="price"
                            class="package-detail__pricing"
                        >
                            <span class="pricing-label">{{ t('widget.global.from') }} </span> <h3 class="package-detail__pricing__h2">
                                {{ formatCurrency(packageItem.pricePerPerson, packageItem.currency, locale) }}
                            </h3> <span class="pricing-label">{{ t('widget.global.per-people') }}</span>
                        </div>
                        <div
                            ref="topAction"
                            class="package-detail__action"
                        >
                            <button
                                v-if="isComingFromBusinessPath"
                                type="button"
                                class="btn btn--primary"
                                @click="emit('showBookView')"
                            >
                                <span>
                                    {{ t('widget.buttons.book-it') }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                <div
                    v-if="!isComingFromBusinessPath"
                    class="package-detail__wrapper"
                >
                    <div class="package-detail__description">
                        <div
                            ref="packageNotes"
                            class="package-detail__description-text"
                        />
                        <br>
                        <a
                            v-if="showReadMode"
                            class="package-detail__description-text__more"
                            type="button"
                            @click="toggleNotes"
                        >
                            <span>
                                {{ t(`${expandNotes ? 'widget.global.read-less' : 'widget.global.read-more'}`) }}
                            </span>
                        </a>
                    </div>
                    <div class="package-detail__action">
                        <button
                            type="button"
                            class="btn btn--primary"
                            @click="emit('showBookView')"
                        >
                            <span>
                                {{ t('widget.buttons.book-it') }}
                            </span>
                        </button>
                    </div>
                </div>

                <div
                    v-if="hasAnyTimeslots || isComingFromBusinessPath"
                    class="package-detail__programs"
                >
                    <div
                        v-if="isComingFromBusinessPath"
                        class="package-detail__programs__day"
                    >
                        <div class="package-detail__programs__title">
                            {{ t('widget.package.program') }}
                        </div>
                        <div class="package-detail__programs__list">
                            <div class="package-detail__timeslot">
                                <div class="package-detail__timeslot__name">
                                    <div class="package-detail__timeslot__time">
                                        <span v-if="selectedTimeRange.min">
                                            {{ selectedTimeRange.min }}
                                        </span>
                                        <template v-if="selectedTimeRange.max">
                                            <span>-</span>
                                            <span>
                                                {{ selectedTimeRange.max }}
                                            </span>
                                        </template>
                                    </div>
                                    <div class="package-detail__timeslot__title">
                                        {{ t('widget.global.labels.general') }}
                                    </div>
                                    <div class="package-detail__timeslot__guest-amounts package-detail__timeslot__guest-amounts--desktop">
                                        <span
                                            v-if="businessGuestAmount"
                                            class="package-detail__timeslot__guest-amounts__span"
                                        >
                                            {{ businessGuestAmount }} {{ t('widget.global.guests') }}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-if="selectedSpaces?.length || selectedProducts?.length"
                                    class="package-detail__timeslot__content"
                                >
                                    <div class="package-detail__timeslot__guest-amounts package-detail__timeslot__guest-amounts--mobile">
                                        <span
                                            v-if="businessGuestAmount"
                                            class="package-detail__timeslot__guest-amounts__span"
                                        >
                                            {{ businessGuestAmount }} {{ t('widget.global.guests') }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="selectedSpaces.length"
                                        class="package-detail__timeslot__spaces"
                                    >
                                        <div
                                            v-for="(space, spaceIndex) in selectedSpaces"
                                            :key="`general-${space.id}-${spaceIndex}`"
                                            class="package-detail__timeslot__space"
                                        >
                                            <span class="package-detail__timeslot__space__span">
                                                {{ space.name }}
                                            </span>
                                            <button
                                                v-if="isOnBusinessPath"
                                                type="button"
                                                class="btn btn--ghost btn--icon btn--thin"
                                                @click="emit('showSpaces')"
                                            >
                                                <Icon
                                                    name="editOutlined"
                                                    size="medium"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        v-if="selectedProducts.length"
                                        class="package-detail__timeslot__products-wrapper"
                                    >
                                        <div class="package-detail__timeslot__included">
                                            {{ t('widget.package.included') }}
                                        </div>

                                        <div class="package-detail__timeslot__products">
                                            <div
                                                v-for="(product, productIndex) in selectedProducts"
                                                :key="`general-${product.id}-${productIndex}`"
                                                class="package-detail__timeslot__product"
                                            >
                                                <div class="package-detail__timeslot__product-img">
                                                    <AdvancedImage
                                                        :media="product.cover"
                                                        :preview="product.cover ? null : DEFAULT_IMG"
                                                        no-crop
                                                        aspect-ratio="1:1"
                                                    />
                                                </div>
                                                <div class="package-detail__timeslot__product-content">
                                                    <div class="package-detail__timeslot__product-name">
                                                        {{ product.name }}
                                                    </div>
                                                    <div
                                                        v-safe-html="getProductNotes(
                                                            combineDescriptions(filterNotes(product.notes ?? [], 3)),
                                                            expandedProducts[`${product.id}-${productIndex}`] || false
                                                        )"
                                                        class="package-detail__timeslot__product-desc"
                                                    />
                                                    <a
                                                        v-if="shouldShowProductReadMore(combineDescriptions(filterNotes(product.notes ?? [], 3)))"
                                                        class="package-detail__timeslot__product-link"
                                                        @click="toggleProductNotes(`${product.id}-${productIndex}`)"
                                                    >
                                                        {{
                                                            t(`${expandedProducts[`${product.id}-${productIndex}`] ? 'widget.global.read-less' : 'widget.global.read-more'}`)
                                                        }}
                                                    </a>
                                                </div>
                                                <div class="package-detail__timeslot__product-price">
                                                    <template v-if="product.pricingStrategy === ProductPricingStrategy.PerGuesttype">
                                                        <div
                                                            v-for="guestType in unitPriceFilteredAmounts(product)"
                                                            :key="`${guestType?.id}-${guestType.guestType.abbreviation}`"
                                                            class="package-detail__timeslot__product-price-init"
                                                        >
                                                            <strong>
                                                                {{ formatCurrency(guestType.price ?? 0) }}
                                                            </strong>
                                                            {{ guestType.guestType.abbreviation }}
                                                        </div>
                                                    </template>
                                                    <div
                                                        v-else
                                                        class="package-detail__timeslot__product-price-init"
                                                    >
                                                        <strong>
                                                            {{ formatCurrency(product.price) }}
                                                        </strong>
                                                        {{ t(`widget.global.pricing-strategy.${product.pricingStrategy}`) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            class="btn btn--normal btn--secondary package-detail__timeslot__catalog"
                                            @click="emit('showProducts')"
                                        >
                                            {{ t('widget.global.catalog') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <template v-else>
                        <div
                            v-for="(day, dayIndex) in packageItem.programme"
                            :key="`${day.name}-day`"
                            class="package-detail__programs__day"
                        >
                            <div class="package-detail__programs__title">
                                {{ packageItem.programme.length > 1 ? t('widget.package.program-day', { day: dayIndex + 1 }) : t('widget.package.program') }}
                            </div>
                            <div class="package-detail__programs__list">
                                <div
                                    v-for="timeslot in day.timeslots"
                                    :key="timeslot.id"
                                    class="package-detail__timeslot"
                                >
                                    <div class="package-detail__timeslot__name">
                                        <div class="package-detail__timeslot__time">
                                            <span v-if="timeslot.startDateTime">
                                                {{ DateTime.dayjsObject(timeslot.startDateTime).format('HH:mm') }}
                                            </span>
                                            <template v-if="timeslot.endDateTime">
                                                <span>
                                                    -
                                                </span>
                                                <span>
                                                    {{ DateTime.dayjsObject(timeslot.endDateTime).format('HH:mm') }}
                                                </span>
                                            </template>
                                        </div>
                                        <div class="package-detail__timeslot__title">
                                            {{ timeslot.name ?? '-' }}
                                        </div>
                                        <div class="package-detail__timeslot__guest-amounts package-detail__timeslot__guest-amounts--desktop">
                                            <span
                                                v-if="guestAmount && packageItem.eventTypes[0]?.defaultGuestType"
                                                class="package-detail__timeslot__guest-amounts__span"
                                            >
                                                {{ guestAmount }} {{ packageItem.eventTypes[0]?.defaultGuestType?.name }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            filterNotes(timeslot.notes ?? [], 3)?.length ||
                                                timeslot.guestAmounts?.length ||
                                                timeslot.spaces?.length ||
                                                timeslot.products?.length
                                        "
                                        class="package-detail__timeslot__content"
                                    >
                                        <div
                                            v-if="filterNotes(timeslot.notes ?? [], 3)?.length"
                                            v-safe-html="formatTextToHtml(combineDescriptions(filterNotes(timeslot.notes ?? [], 3)))"
                                            class="package-detail__timeslot__description"
                                        />
                                        <div class="package-detail__timeslot__guest-amounts package-detail__timeslot__guest-amounts--mobile">
                                            <span
                                                v-if="guestAmount && packageItem.eventTypes[0]?.defaultGuestType"
                                                class="package-detail__timeslot__guest-amounts__span"
                                            >
                                                {{ guestAmount }} {{ packageItem.eventTypes[0]?.defaultGuestType?.name }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="timeslot.spaces && timeslot.spaces.length"
                                            class="package-detail__timeslot__spaces"
                                        >
                                            <template v-if="timeslot.spaces && timeslot.spaces.length">
                                                <div
                                                    v-for="(timeslotSpace, idx) in timeslot.spaces"
                                                    :key="idx"
                                                    class="package-detail__timeslot__space"
                                                >
                                                    <span class="package-detail__timeslot__space__span">
                                                        {{ getTimeslotSpaceName(timeslotSpace as TimeSlotSpaceTyping) }}
                                                    </span>
                                                    <button
                                                        v-if="isOnBusinessPath"
                                                        type="button"
                                                        class="btn btn--ghost btn--icon btn--thin"
                                                        @click="emit('showSpaces')"
                                                    >
                                                        <Icon
                                                            name="editOutlined"
                                                            size="medium"
                                                        />
                                                    </button>
                                                </div>
                                            </template>
                                        </div>
                                        <div
                                            v-if="timeslot.products && timeslot.products.length"
                                            class="package-detail__timeslot__products-wrapper"
                                        >
                                            <div class="package-detail__timeslot__included">
                                                {{ t('widget.package.included') }}
                                            </div>

                                            <div class="package-detail__timeslot__products">
                                                <div
                                                    v-for="(product, productIndex) in getTimeslotProducts(timeslot.products)"
                                                    :key="`${timeslot.id}-${product.id}-${productIndex}`"
                                                    class="package-detail__timeslot__product"
                                                >
                                                    <div class="package-detail__timeslot__product-img">
                                                        <AdvancedImage
                                                            :media="product.overrideCover"
                                                            :preview="product.overrideCover ? null : DEFAULT_IMG"
                                                            no-crop
                                                            aspect-ratio="1:1"
                                                        />
                                                    </div>
                                                    <div class="package-detail__timeslot__product-content">
                                                        <div class="package-detail__timeslot__product-name">
                                                            {{ getTimeslotProductName(product as unknown as TimeSlotProductTyping) }}
                                                        </div>
                                                        <div
                                                            v-safe-html="getProductNotes(
                                                                getTimeslotProductNotes(product as unknown as TimeSlotProductTyping),
                                                                expandedProducts[`${timeslot.id}-${product.id}-${productIndex}`] || false
                                                            )"
                                                            class="package-detail__timeslot__product-desc"
                                                        />
                                                        <a
                                                            v-if="shouldShowProductReadMore(
                                                                getTimeslotProductNotes(product as unknown as TimeSlotProductTyping)
                                                            )"
                                                            class="package-detail__timeslot__product-link"
                                                            @click="toggleProductNotes(`${timeslot.id}-${product.id}-${productIndex}`)"
                                                        >
                                                            {{
                                                                t(`${expandedProducts[`${timeslot.id}-${product.id}-${productIndex}`] ? 'widget.global.read-less' : 'widget.global.read-more'}`)
                                                            }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
        <div
            class="package-detail__book"
            :class="{'package-detail__book--show': showBookBar}"
        >
            <div
                v-if="!isOnBusinessPath"
                class="package-detail__book-price"
            >
                <h3>{{ formatCurrency(packageItem.pricePerPerson, packageItem.currency, locale) }} </h3> <span class="pricing-label">{{ t('widget.global.per-people') }}</span>
            </div>
            <button
                type="button"
                class="btn btn--primary"
                @click="emit('showBookView')"
            >
                <span>
                    {{ t('widget.buttons.book-it') }}
                </span>
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.package-detail {
    position: relative;
    z-index: var(--zindex-popup);
    display: flex;
    flex-direction: column;
    background-color: rgb(243 243 243);
    padding-block-end: var(--spacing-17x);

    @include min-width(md) {
        padding-block-end: var(--spacing-20);
    }

    &__header {
        line-height: 1;
        margin-block: var(--spacing-04);
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }

    &__action {
        display: none;
        margin-inline-start: auto;

        @include min-width(md) {
            display: block;
        }
    }

    &__book {
        position: fixed;
        display: flex;
        align-items: center;
        inset-block-end: var(--spacing-02);
        inset-inline-end: var(--spacing-03);
        margin-inline-start: auto;
        opacity: 0;
        transition: all 250ms ease-in-out;
        will-change: auto;

        &--show {
            opacity: 1;
            transform: translateY(calc(0px - var(--spacing-02)));

            @include min-width(md) {
                transform: translateY(calc(0px - var(--spacing-05)));
            }

            @include min-width(lg) {
                transform: translateY(calc(0px - var(--spacing-08)));
            }
        }

        @include min-width(md) {
            inset-inline-end: var(--spacing-05);
        }

        @include min-width(lg) {
            inset-inline-end: var(--spacing-08);
        }

        &-price {
            display: flex;
            flex-grow: 1;
            align-items: center;
            border: solid 1px var(--color-pure-white);
            border-radius: var(--border-radius-sm) 0 0 var(--border-radius-sm);
            background-color: var(--background-primary);
            block-size: calc(var(--size-14) - 2px);
            padding-inline: var(--spacing-04) var(--spacing-07);
            pointer-events: none;
            transform: translateX(30px);

            @include min-width(md) {
                block-size: var(--size-14);
            }

            @include min-width(lg) {
                block-size: calc(var(--size-15) + 2px);
            }

            h3 {
                font-size: var(--font-h3);
                font-weight: var(--font-weight-semi-bold);
                letter-spacing: var(--letter-spacing-sm);
                line-height: var(--line-height-120);
                margin-inline-end:  var(--spacing-01);
            }
        }
    }

    &__wrapper {
        position: relative;
        display: flex;
        align-items: flex-start;

        @include min-width(md) {
            min-block-size: var(--size-9);
        }
    }

    &__description {
        font-size: var(--font-regular);
        font-weight: var(--font-weight-light);
        line-height: var(--line-height-150);

        @include min-width(md) {
            max-inline-size: calc(100% - 300px);
        }
    }

    &__description-text {
        font-size: var(--font-regular);

        &__more span {
            font-size: var(--font-regular);
            font-weight: var(--font-weight-medium);
            text-decoration: underline;
        }
    }

    &__timeslot {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-03);
        padding-block-end: var(--spacing-03);

        @include min-width(md) {
            gap: var(--spacing-04);
        }

        &:not(:last-child) {
            border-block-end: solid 1px var(--color-pure-white);
        }

        &__time {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: var(--font-regular);
            inline-size: 84px;

            @include min-width(md) {
                    inline-size: 104px;
            }

            span {
                font-size: var(--font-regular);
                font-weight: var(--font-weight-medium);
            }

            span:first-child {
                margin-inline-end: auto;
                text-align: start;
            }

            span:last-child {
                margin-inline-start: auto;
                text-align: end;
            }

        }

        &__title {
            flex-grow: 1;
            font-size: var(--font-regular);
            padding-inline: var(--spacing-03);
        }

        &__guest-amounts {
            display: flex;
            align-items: center;
            gap: var(--spacing-02);

            &--mobile {
                display: flex;

                @include min-width(md) {
                    display: none;
                }
            }

            &--desktop {
                display: none;

                @include min-width(md) {
                    display: flex;
                }
            }

            &__span {
                font-size: var(--font-regular);
                font-weight: var(--font-weight-medium);
                line-height: var(--line-height-150);
            }
        }

        &__time, &__title {
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-150);
        }

        &__included {
            display: flex;
            align-items: center;
            block-size: var(--size-4);
            color: var(--text-secondary);
            font-size: var(--font-regular);
            font-weight: var(--font-weight-light);
            line-height: var(--line-height-150);
        }

        &__name {
            display: flex;
            align-items: center;
            padding-block-start: var(--spacing-03);
            padding-inline: var(--spacing-03);
        }

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-03);
            padding-inline: var(--spacing-03);

            @include min-width(md) {
                gap: var(--spacing-04);
                padding-inline-start: 136px;
            }
        }

        &__description {
            font-size: var(--font-regular);
            font-weight: var(--font-weight-light);
            line-height: var(--line-height-150);
        }

        &__spaces {
            display: grid;
            gap: calc(var(--spacing-02) + 2px);
            grid-template-columns: 1fr 1fr;

            @include min-width(md) {
                grid-template-columns: 1fr 1fr 1fr;
            }
        }

        &__space {
            display: inline-flex;
            overflow: hidden;
            align-items: center;
            justify-content: space-between;
            border: solid 1px var(--color-border-quaternary);
            border-radius: var(--border-radius-xs);
            background-color: var(--background-secondary);
            inline-size: 100%;
            min-block-size: var(--size-7);
            padding-block: var(--spacing-02);
            padding-inline: var(--spacing-03);
            transition: all 250ms ease-in-out;

            &:has(:focus-within) {
                background-color: var(--color-pure-white);
            }

            &__span {
                overflow: hidden;
                font-size: var(--font-regular);
                font-weight: var(--font-weight-light);
                line-height: var(--line-height-150);
                text-overflow: ellipsis;
                white-space: nowrap;

                &:focus {
                    overflow: visible;
                    outline: none;
                    text-overflow: initial;
                }
            }
        }

        &__products {
            display: grid;
            gap: var(--spacing-02);
            grid-template-columns: 1fr;

            &-wrapper {
                display: flex;
                flex-direction: column;
                gap: var(--spacing-3x);
            }

            @include min-width(md) {
                grid-template-columns: 1fr 1fr;
            }
        }

        &__product {
            position: relative;
            display: flex;
            overflow: hidden;
            align-items: flex-start;
            padding: var(--spacing-02);
            border-radius: var(--border-radius-sm);
            background-color: var(--color-moon-white);
            cursor: pointer;
            gap: var(--spacing-02);
            min-inline-size: 0;
            transition: all 250ms ease-in-out;

            @include min-width(md) {
                gap: var(--spacing-03);
            }

            &:hover {
                background-color: var(--background-secondary-highlight);
            }

            &-img {
                overflow: hidden;
                border-radius: var(--border-radius-sm);
                aspect-ratio: 1/1;
                block-size: var(--size-10);
                min-inline-size: var(--size-10);

                @include min-width(md) {
                    block-size: var(--size-15);
                    min-inline-size: var(--size-15);
                }

                @include min-width(lg) {
                    block-size: var(--size-16);
                    min-inline-size: var(--size-16);
                }
            }

            &-content {
                display: flex;
                overflow: hidden;
                flex-direction: column;
            }

            &-name {
                font-size: var(--font-regular);
                font-weight: var(--font-weight-medium);
            }

            &-desc {
                color: var(--text-secondary);
                font-size: var(--font-regular);
                font-weight: var(--font-weight-light);
                max-inline-size: 80%;

                @include min-width(md) {
                    max-inline-size: 70%;
                }

                @include min-width(lg) {
                    max-inline-size: 74%;
                }
            }

            &-link {
                font-size: var(--font-regular);
                font-weight: var(--font-weight-medium);
                margin-block-start: var(--spacing-02);
                text-decoration: underline;
            }

            &-price {
                position: absolute;
                display: flex;
                flex-direction: column;
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-primary-highlight);
                inset-block-start: var(--spacing-02);
                inset-inline-end: var(--spacing-02);
                padding-block: var(--spacing-01);
                padding-inline: var(--spacing-02);

                &-init {
                    font-size: 10px;
                }
            }
        }

        &__catalog {
            align-self: flex-start;
        }
    }

    &__programs {
        display: flex;
        flex-direction: column;

        &__day {
            margin-block-start: var(--spacing-05);
        }

        &__title {
            display: flex;
            align-items: center;
            font-size: var(--font-regular);
            font-weight: var(--font-weight-light);
            line-height: var(--line-height-150);
            padding-block: var(--spacing-03);
        }

        &__list {
            display: flex;
            flex-direction: column;
            border: solid 1px var(--color-pure-white);
            border-radius: var(--border-radius-sm);
        }
    }

    &__content {
        flex: 1;
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            padding-inline: var(--spacing-05);
        }

        @include min-width(lg) {
            padding-inline: var(--spacing-07);
        }
    }

    &__img {
        position: relative;
        overflow: hidden;
        border-radius: var(--border-radius-md);
        background-color: var(--color-border-quaternary);
        block-size: 220px;
        inline-size: 100%;

        @include min-width(md) {
            block-size: 301px;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        padding-block: var(--spacing-05);

        @include min-width(md) {
            padding-block: var(--spacing-06);
        }
    }

    &__pricing {
        display: inline-flex;
        align-items: flex-end;
        gap: var(--spacing-01);

        &__h2 {
            font-size: var(--font-h2);
            font-weight: var(--font-weight-semi-bold);
            letter-spacing: var(--letter-spacing-sm);
            line-height: var(--line-height-120);
        }
    }

    &__title {
        display: flex;
        align-items: center;
        margin-block-end: var(--spacing-03);

        > div {
            display: none;

            @include min-width(md) {
                display: inline;
            }
        }

        h2 {
            overflow: hidden;
            flex-grow: 1;
            font-size: var(--font-h2);
            font-weight: var(--font-weight-semi-bold);
            letter-spacing: var(--letter-spacing-sm);
            line-height: var(--line-height-120);
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .pricing-label {
        font-size: var(--font-regular);
        font-weight: var(--font-weight-light);
        line-height: var(--line-height-150);
    }
}
</style>
